// frontend/src/pages/Settings.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  FormGroup,
  Slider,
  Grid,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
  Info as InfoIcon
} from '@mui/icons-material';

import ThemeSwitcher from '../components/ThemeSwitcher';
import { useThemeSettings } from "../contexts/ThemeContext";
import * as indexedDB from '../utils/indexedDB';
import * as tokenEncryption from '../utils/tokenEncryption';

/**
 * Get auth token from secure storage with fallback
 */
const getTokenFromStorage = async (key) => {
  try {
    // Try IndexedDB first
    if (indexedDB.isAvailable()) {
      const encryptedToken = await indexedDB.getItem(key);
      if (encryptedToken) {
        const decryptedToken = await tokenEncryption.decryptToken(encryptedToken);
        if (decryptedToken) {
          return decryptedToken;
        }
      }
    }

    // Fallback to localStorage if IndexedDB fails
    const localToken = localStorage.getItem(key);
    if (localToken) {
      return localToken;
    }

    return null;
  } catch (error) {
    console.error('Error retrieving token:', error);
    // Last resort: try localStorage directly
    return localStorage.getItem(key);
  }
};

const TabPanel = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const Settings = () => {
  const { t } = useThemeSettings();
  const [tabValue, setTabValue] = useState(0);
  const [settings, setSettings] = useState({
    language: 'en',
    notifications: true,
    autoDelete: false,
    retentionDays: 365,
    maxFileSize: 104857600,
    allowedTypes: 'all'
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState({
    emailEnabled: true,
    pushEnabled: true,
    respectCriticalOverride: true,
    emailDelayMinutes: 30,
    typePreferences: {}
  });

  // Notification types with friendly names
  const notificationTypes = [
    { key: 'ticket_created', label: t ? t('NotificationTypes.ticket_created') : 'New Tickets', category: t ? t('NotificationCategories.tickets') : 'Tickets' },
    { key: 'ticket_updated', label: t ? t('NotificationTypes.ticket_updated') : 'Ticket Updates', category: t ? t('NotificationCategories.tickets') : 'Tickets' },
    { key: 'ticket_resolved', label: t ? t('NotificationTypes.ticket_resolved') : 'Ticket Resolutions', category: t ? t('NotificationCategories.tickets') : 'Tickets' },
    { key: 'ticket_intervention', label: t ? t('NotificationTypes.ticket_intervention') : 'Physical Interventions', category: t ? t('NotificationCategories.tickets') : 'Tickets' },
    { key: 'ticket_message', label: t ? t('NotificationTypes.ticket_message') : 'Ticket Messages', category: t ? t('NotificationCategories.tickets') : 'Tickets' },
    { key: 'device_offline', label: t ? t('NotificationTypes.device_offline') : 'Device Offline Alerts', category: t ? t('NotificationCategories.devices') : 'Devices' },
    { key: 'device_online', label: t ? t('NotificationTypes.device_online') : 'Device Online Notifications', category: t ? t('NotificationCategories.devices') : 'Devices' },
    { key: 'file_uploaded', label: t ? t('NotificationTypes.file_uploaded') : 'File Uploads', category: t ? t('NotificationCategories.files') : 'Files' },
    { key: 'file_shared', label: t ? t('NotificationTypes.file_shared') : 'Files Shared with You', category: t ? t('NotificationCategories.files') : 'Files' },
    { key: 'file_deleted', label: t ? t('NotificationTypes.file_deleted') : 'File Deletions', category: t ? t('NotificationCategories.files') : 'Files' },
    { key: 'virus_detected', label: t ? t('NotificationTypes.virus_detected') : 'Virus Detected', category: t ? t('NotificationCategories.security') : 'Security' },
    { key: 'storage_warning', label: t ? t('NotificationTypes.storage_warning') : 'Storage Warnings', category: t ? t('NotificationCategories.system') : 'System' },
    { key: 'storage_critical', label: t ? t('NotificationTypes.storage_critical') : 'Storage Critical Alerts', category: t ? t('NotificationCategories.system') : 'System' },
    { key: 'system_alert', label: t ? t('NotificationTypes.system_alert') : 'System Alerts', category: t ? t('NotificationCategories.system') : 'System' }
  ];

  // Load notification preferences
  useEffect(() => {
    loadNotificationPreferences();
  }, []);

  const loadNotificationPreferences = async () => {
    try {
      const token = await getTokenFromStorage('auth_token');
      const response = await axios.get('https://api.caro.care/api/notifications/preferences', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setNotificationPreferences(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
      showSnackbar(t ? t('NotificationSettings.load_failed') : 'Failed to load notification preferences', 'error');
    }
  };

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setSettings(prev => ({
      ...prev,
      [name]: event.target.type === 'checkbox' ? checked : value
    }));
  };

  const handleNotificationPrefChange = (field, value) => {
    setNotificationPreferences(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTypePreferenceChange = (type, channel, value) => {
    setNotificationPreferences(prev => ({
      ...prev,
      typePreferences: {
        ...prev.typePreferences,
        [type]: {
          ...(prev.typePreferences[type] || {}),
          [channel]: value
        }
      }
    }));
  };

  const saveNotificationPreferences = async () => {
    setLoading(true);
    try {
      const token = await getTokenFromStorage('auth_token');
      const response = await axios.put('https://api.caro.care/api/notifications/preferences', notificationPreferences, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        showSnackbar(t ? t('NotificationSettings.save_success') : 'Notification preferences saved successfully', 'success');
      }
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
      showSnackbar(t ? t('NotificationSettings.save_failed') : 'Failed to save notification preferences', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetNotificationPreferences = async () => {
    if (!window.confirm(t ? t('NotificationSettings.confirm_reset') : 'Are you sure you want to reset all notification preferences to defaults?')) {
      return;
    }

    setLoading(true);
    try {
      const token = await getTokenFromStorage('auth_token');
      const response = await axios.post('https://api.caro.care/api/notifications/preferences/reset', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setNotificationPreferences(response.data.data);
        showSnackbar(t ? t('NotificationSettings.reset_success') : 'Notification preferences reset to defaults', 'success');
      }
    } catch (error) {
      console.error('Failed to reset notification preferences:', error);
      showSnackbar(t ? t('NotificationSettings.reset_failed') : 'Failed to reset notification preferences', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Group notification types by category
  const groupedTypes = notificationTypes.reduce((acc, type) => {
    if (!acc[type.category]) {
      acc[type.category] = [];
    }
    acc[type.category].push(type);
    return acc;
  }, {});

  return (
    <Box
      className="page-transition-enter"
      sx={{
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>

      <Typography variant="h4" className="slide-down" gutterBottom>{t ? t("Layout.settings") : "Settings"}

      </Typography>

      <Paper
        className="card-animated card-enter"
        sx={{
          mt: 3,
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box'
        }}>
        
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label={t ? t("Settings.preferences") : "Preferences"} />
          <Tab label={t ? t("Settings.notifications") : "Notifications"} />
        </Tabs>

        <Box sx={{ p: 3, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" gutterBottom>{t ? t("Settings.personal_preferences") : "Personal preferences"}

            </Typography>

            <Box fullWidth sx={{ mb: 3, width: '100%', maxWidth: '100%' }}>
              <ThemeSwitcher />
            </Box>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>
              {t ? t('NotificationSettings.title') : 'Notification Preferences'}
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              {t ? t('NotificationSettings.info_message') : 'Configure how you receive notifications. Critical notifications will always be sent regardless of these settings.'}
            </Alert>

            {/* Global Settings */}
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                mb: 3,
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.02)'
                  : 'rgba(0, 0, 0, 0.01)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t ? t('NotificationSettings.global_settings') : 'Global Settings'}
                </Typography>
                <Chip
                  size="small"
                  label={t ? t('NotificationSettings.global_chip') : 'Master Controls'}
                  color="primary"
                  variant="outlined"
                />
              </Box>

              <FormGroup>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  px: 2,
                  mb: 1,
                  borderRadius: 1,
                  background: (theme) => notificationPreferences.emailEnabled
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(76, 175, 80, 0.1)'
                      : 'rgba(76, 175, 80, 0.05)'
                    : 'transparent',
                  border: '1px solid',
                  borderColor: (theme) => notificationPreferences.emailEnabled
                    ? theme.palette.success.main
                    : theme.palette.divider
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EmailIcon color={notificationPreferences.emailEnabled ? 'success' : 'disabled'} />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {t ? t('NotificationSettings.enable_email') : 'Email Notifications'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {notificationPreferences.emailEnabled
                          ? (t ? t('NotificationSettings.email_enabled_desc') : 'Receive notifications via email')
                          : (t ? t('NotificationSettings.email_disabled_desc') : 'Email notifications are turned off')}
                      </Typography>
                    </Box>
                  </Box>
                  <Switch
                    checked={notificationPreferences.emailEnabled}
                    onChange={(e) => handleNotificationPrefChange('emailEnabled', e.target.checked)}
                    color="success"
                  />
                </Box>

                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  px: 2,
                  mb: 1,
                  borderRadius: 1,
                  background: (theme) => notificationPreferences.pushEnabled
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(33, 150, 243, 0.1)'
                      : 'rgba(33, 150, 243, 0.05)'
                    : 'transparent',
                  border: '1px solid',
                  borderColor: (theme) => notificationPreferences.pushEnabled
                    ? theme.palette.primary.main
                    : theme.palette.divider
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {notificationPreferences.pushEnabled ? (
                      <NotificationsIcon color="primary" />
                    ) : (
                      <NotificationsOffIcon color="disabled" />
                    )}
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {t ? t('NotificationSettings.enable_push') : 'Push Notifications'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {notificationPreferences.pushEnabled
                          ? (t ? t('NotificationSettings.push_enabled_desc') : 'Receive instant push notifications')
                          : (t ? t('NotificationSettings.push_disabled_desc') : 'Push notifications are turned off')}
                      </Typography>
                    </Box>
                  </Box>
                  <Switch
                    checked={notificationPreferences.pushEnabled}
                    onChange={(e) => handleNotificationPrefChange('pushEnabled', e.target.checked)}
                  />
                </Box>

                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  px: 2,
                  borderRadius: 1,
                  background: 'transparent',
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Tooltip title={t ? t('NotificationSettings.critical_tooltip') : 'Critical notifications will always be delivered regardless of your preferences'}>
                      <InfoIcon color="info" />
                    </Tooltip>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {t ? t('NotificationSettings.allow_critical_override') : 'Allow Critical Override'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t ? t('NotificationSettings.critical_desc') : 'Critical notifications bypass your preferences'}
                      </Typography>
                    </Box>
                  </Box>
                  <Switch
                    checked={notificationPreferences.respectCriticalOverride}
                    onChange={(e) => handleNotificationPrefChange('respectCriticalOverride', e.target.checked)}
                    color="warning"
                  />
                </Box>
              </FormGroup>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                {t ? t('NotificationSettings.email_delay_title') : 'Email Delay (minutes)'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t ? t('NotificationSettings.email_delay_description') : 'If you receive a push notification, wait this long before sending email (only if notification is unread)'}
              </Typography>
              <Box sx={{ px: 2, mt: 2 }}>
                <Slider
                  value={notificationPreferences.emailDelayMinutes}
                  onChange={(e, value) => handleNotificationPrefChange('emailDelayMinutes', value)}
                  min={0}
                  max={240}
                  step={15}
                  marks={[
                    { value: 0, label: '0m' },
                    { value: 60, label: '1h' },
                    { value: 120, label: '2h' },
                    { value: 180, label: '3h' },
                    { value: 240, label: '4h' }
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value} minutes`}
                />
              </Box>
            </Paper>

            {/* Per-Type Settings */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t ? t('NotificationSettings.type_settings') : 'Notification Type Settings'}
              </Typography>
              <Chip
                size="small"
                label={t ? t('NotificationSettings.fine_tune') : 'Fine-tune'}
                variant="outlined"
              />
            </Box>

            {Object.entries(groupedTypes).map(([category, types]) => (
              <Paper
                key={category}
                variant="outlined"
                sx={{
                  p: 2.5,
                  mb: 2,
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.02)'
                    : 'rgba(0, 0, 0, 0.01)'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                    {category}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${types.length} ${types.length === 1 ? 'type' : 'types'}`}
                    sx={{ height: 20 }}
                  />
                </Box>
                <Grid container spacing={1.5}>
                  {types.map((type) => {
                    const typePref = notificationPreferences.typePreferences[type.key] || { email: true, push: true };
                    const emailActive = typePref.email !== false && notificationPreferences.emailEnabled;
                    const pushActive = typePref.push !== false && notificationPreferences.pushEnabled;

                    return (
                      <Grid item xs={12} key={type.key}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': {
                            background: (theme) => theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.03)'
                              : 'rgba(0, 0, 0, 0.02)'
                          }
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {type.label}
                            </Typography>
                            {emailActive || pushActive ? (
                              <Chip
                                size="small"
                                label={emailActive && pushActive ? 'Both' : emailActive ? 'Email' : 'Push'}
                                color="success"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            ) : (
                              <Chip
                                size="small"
                                label="Off"
                                color="default"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title={!notificationPreferences.emailEnabled ? 'Email notifications disabled globally' : ''}>
                              <span>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={emailActive}
                                      onChange={(e) => handleTypePreferenceChange(type.key, 'email', e.target.checked)}
                                      disabled={!notificationPreferences.emailEnabled}
                                      color="success"
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <EmailIcon sx={{ fontSize: 16 }} />
                                      <Typography variant="caption">
                                        {t ? t('NotificationSettings.email_label') : 'Email'}
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{ mr: 0 }}
                                />
                              </span>
                            </Tooltip>
                            <Tooltip title={!notificationPreferences.pushEnabled ? 'Push notifications disabled globally' : ''}>
                              <span>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={pushActive}
                                      onChange={(e) => handleTypePreferenceChange(type.key, 'push', e.target.checked)}
                                      disabled={!notificationPreferences.pushEnabled}
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <NotificationsIcon sx={{ fontSize: 16 }} />
                                      <Typography variant="caption">
                                        {t ? t('NotificationSettings.push_label') : 'Push'}
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{ mr: 0 }}
                                />
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            ))}

            {/* Action Buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={saveNotificationPreferences}
                disabled={loading}
              >
                {t ? t('NotificationSettings.save_button') : 'Save Preferences'}
              </Button>
              <Button
                variant="outlined"
                onClick={resetNotificationPreferences}
                disabled={loading}
              >
                {t ? t('NotificationSettings.reset_button') : 'Reset to Defaults'}
              </Button>
            </Box>
          </TabPanel>
        </Box>

      </Paper>
    </Box>);

};

export default Settings;